import { useState, useEffect, useCallback } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, ProgressBar } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import AccountSearchModal from './AccountSearchModal'

const extractAccountsList = (data) => {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.results)) return data.results
  return data?.results ?? []
}

const fetchAccountsPage = async (url) => {
  const response = await api.get(url)
  return extractAccountsList(response.data)
}

// Componente para selector de nivel dinámico
const AccountLevelSelector = ({ parentId, levelIndex, selectedValue, onSelect, onDeselect, label, user, selectedClient, clients }) => {
  const { data: children, isLoading } = useQuery(
    ['chart-of-accounts-children', parentId, selectedClient],
    async () => {
      if (!parentId) return []
      let url = `/accounting/accounts/?is_active=true&parent=${parentId}&page_size=500`
      if (selectedClient) url += `&cliente_id=${selectedClient}`
      const response = await api.get(url)
      return response.data.results || response.data || []
    },
    { enabled: !!parentId && !!user }
  )

  const activeChildren = children?.filter(acc => acc.is_active !== false) || []
  if (!activeChildren || activeChildren.length === 0) return null

  const getClienteIndicator = (clientId) => {
    if (!clientId) return ''
    const c = clients?.find(x => x.id == clientId)
    const prefijo = (c?.clave_prefijo || '').trim()
    return prefijo ? `(Cliente - ${prefijo})` : '(Cliente)'
  }

  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      {isLoading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <Form.Select 
          value={selectedValue ? String(selectedValue) : ''}
          onChange={(e) => {
            if (e.target.value) {
              onSelect(parseInt(e.target.value, 10))
            } else {
              onDeselect()
            }
          }}
        >
          <option value="">Ninguna (crear en este nivel)</option>
          {activeChildren.map((account, index) => {
            const optionValue = String(account.id)
            const key = account.id || `account-${index}`
            return (
              <option key={key} value={optionValue}>
                {account.code} - {account.name} {account.client_id ? getClienteIndicator(account.client_id) : ''}
              </option>
            )
          })}
        </Form.Select>
      )}
      <Form.Text className="text-muted">
        {levelIndex === 0 
          ? 'Selecciona una sub-cuenta para crear un nieto, o deja vacío para crear un hijo de la cuenta base.'
          : `Selecciona una sub-cuenta para crear un nivel más profundo, o deja vacío para crear en este nivel.`
        }
      </Form.Text>
    </Form.Group>
  )
}

const ClientCustomization = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { clientId: selectedClient, setClientId: setSelectedClient, selectDisabled } = useSyncedLocalClientId()
  const [showForm, setShowForm] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [bulkResult, setBulkResult] = useState(null)
  const [selectedCustomization, setSelectedCustomization] = useState(null)
  // Cambiar a un array de niveles seleccionados: [baseId, child1Id, child2Id, ...]
  const [selectedAccountPath, setSelectedAccountPath] = useState([])
  const [autoGeneratedCode, setAutoGeneratedCode] = useState('')
  const [showAccountSearchModal, setShowAccountSearchModal] = useState(false)
  const [customizations, setCustomizations] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false)
  const [loadProgress, setLoadProgress] = useState({ done: 0, total: 0 })
  const [customizationsError, setCustomizationsError] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)

  const refreshCustomizations = useCallback(() => {
    setReloadToken((t) => t + 1)
  }, [])

  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-customization',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Personalizaciones:
  // - Con filtro: una sola petición del cliente.
  // - Sin filtro: primer plano = cuentas generales; background = por cliente (evita OOM del listado masivo).
  useEffect(() => {
    if (!user) return undefined
    let cancelled = false

    const run = async () => {
      setCustomizationsError(null)
      setIsLoading(true)
      setIsBackgroundLoading(false)
      setLoadProgress({ done: 0, total: 0 })

      try {
        if (selectedClient) {
          const rows = await fetchAccountsPage(
            `/accounting/accounts/?client_accounts_only=true&is_active=true&page_size=500&cliente_id=${selectedClient}`
          )
          if (!cancelled) {
            setCustomizations(rows)
            setIsLoading(false)
          }
          return
        }

        const generales = await fetchAccountsPage(
          '/accounting/accounts/?is_active=true&page_size=500'
        )
        if (cancelled) return
        setCustomizations(generales)
        setIsLoading(false)

        if (!clients || clients.length === 0) return

        setIsBackgroundLoading(true)
        const clientIds = clients.map((c) => c.id)
        setLoadProgress({ done: 0, total: clientIds.length })

        let done = 0
        let cursor = 0
        const concurrency = 3

        const worker = async () => {
          while (cursor < clientIds.length) {
            if (cancelled) return
            const idx = cursor
            cursor += 1
            const cid = clientIds[idx]
            try {
              const rows = await fetchAccountsPage(
                `/accounting/accounts/?client_accounts_only=true&is_active=true&page_size=500&cliente_id=${cid}`
              )
              if (cancelled) return
              if (rows.length > 0) {
                setCustomizations((prev) => {
                  const existing = new Set(prev.map((x) => x.id))
                  const merged = [...prev]
                  for (const r of rows) {
                    if (!existing.has(r.id)) merged.push(r)
                  }
                  return merged
                })
              }
            } catch (err) {
              console.error(`Error cargando personalizaciones del cliente ${cid}`, err)
            } finally {
              done += 1
              if (!cancelled) setLoadProgress({ done, total: clientIds.length })
            }
          }
        }

        await Promise.all(
          Array.from({ length: Math.min(concurrency, clientIds.length) }, () => worker())
        )
        if (!cancelled) setIsBackgroundLoading(false)
      } catch (err) {
        if (!cancelled) {
          setCustomizationsError(err)
          setIsLoading(false)
          setIsBackgroundLoading(false)
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [user, selectedClient, clients, reloadToken])

  // Cuentas raíz para selector (con cliente_id para incluir cuentas del cliente en el árbol)
  const { data: baseAccounts, isLoading: loadingBaseAccounts } = useQuery(
    ['chart-of-accounts-roots', selectedClient],
    async () => {
      let url = '/accounting/accounts/?is_active=true&roots_only=true&page_size=500'
      if (selectedClient) url += `&cliente_id=${selectedClient}`
      const response = await api.get(url)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener siguiente consecutivo cuando cambia la selección
  // Lógica para determinar qué cuenta_hijo_id enviar:
  // - Si hay 1 nivel (solo base): no enviar cuenta_hijo_id -> genera hijo directo de la base
  // - Si hay 2 niveles (base + sub-cuenta): enviar el segundo nivel -> genera nieto (nivel 3)
  // - Si hay 3+ niveles (base + sub-cuenta + nieto): enviar el último nivel -> genera bisnieto (nivel 4+)
  // Ejemplo: [400] -> genera hijo directo (404)
  // Ejemplo: [400, 403] -> genera nieto de 403 (403-01)
  // Ejemplo: [400, 403, 403-01] -> genera bisnieto de 403-01 (403-01-001)
  // parent_id = cuenta bajo la cual crear (última en el path)
  const parentIdForNew = selectedAccountPath.length > 0 ? selectedAccountPath[selectedAccountPath.length - 1] : null
  const { data: siguienteCodigo, isLoading: loadingCodigo } = useQuery(
    ['siguiente-consecutivo', selectedClient, parentIdForNew],
    async () => {
      if (!selectedClient || !parentIdForNew) return null
      const params = new URLSearchParams({
        cliente_id: selectedClient,
        parent_id: parentIdForNew
      })
      const response = await api.get(`/accounting/accounts/siguiente_consecutivo/?${params.toString()}`)
      return response.data.siguiente_codigo
    },
    { 
      enabled: !!selectedClient && !!parentIdForNew && !!user && !selectedCustomization
    }
  )

  // Actualizar el código auto-generado cuando cambia siguienteCodigo
  useEffect(() => {
    if (siguienteCodigo && !selectedCustomization) {
      setAutoGeneratedCode(siguienteCodigo)
    }
  }, [siguienteCodigo, selectedCustomization])

  const buildAccountPath = async (accountId) => {
    if (!accountId) return []
    try {
      const url = `/accounting/accounts/${accountId}/ruta_completa/` + (selectedClient ? `?cliente_id=${selectedClient}` : '')
      const response = await api.get(url)
      return response.data.ruta || []
    } catch (err) {
      console.error('Error al obtener ruta de cuenta:', err)
      return [accountId]
    }
  }

  const handleAccountSearchSelect = async (cuenta) => {
    const accountId = cuenta?.cuenta_base_id ?? cuenta?.id
    if (!accountId) return
    try {
      const path = await buildAccountPath(accountId)
      const normalizedPath = path.length > 0
        ? path.map(id => (typeof id === 'number' ? id : parseInt(id, 10)) || id)
        : [typeof accountId === 'number' ? accountId : parseInt(accountId, 10) || accountId]
      setSelectedAccountPath(normalizedPath)
      setShowAccountSearchModal(false)
    } catch (err) {
      toast.error('Error al cargar la cuenta seleccionada')
    }
  }

  // Mutación para crear/actualizar cuenta cliente
  const customizationMutation = useMutation(
    async (data) => {
      if (data.id) {
        return api.put(`/accounting/accounts/${data.id}/`, data)
      } else {
        return api.post('/accounting/accounts/', data)
      }
    },
    {
      onSuccess: () => {
        refreshCustomizations()
        queryClient.invalidateQueries(['chart-of-accounts-children'])
        queryClient.invalidateQueries(['chart-of-accounts-roots'])
        setShowForm(false)
        setSelectedCustomization(null)
        toast.success('Personalización guardada exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al guardar'
        toast.error(message)
      }
    }
  )

  const inactivateMutation = useMutation(
    async (account) => {
      return api.put(`/accounting/accounts/${account.id}/`, {
        code: account.code,
        name: account.name,
        account_type: account.account_type,
        parent: account.parent_id || account.parent || null,
        client: account.client_id || account.client,
        client_id: account.client_id || account.client,
        is_active: false,
        is_deductible: account.is_deductible,
      })
    },
    {
      onSuccess: () => {
        refreshCustomizations()
        queryClient.invalidateQueries(['chart-of-accounts-children'])
        queryClient.invalidateQueries(['chart-of-accounts-roots'])
        setShowForm(false)
        setSelectedCustomization(null)
        toast.success('Cuenta inactivada')
      },
      onError: (error) => {
        const message =
          error.response?.data?.error ||
          error.response?.data?.detail ||
          error.response?.data?.message ||
          'No se pudo inactivar la cuenta'
        toast.error(typeof message === 'string' ? message : 'No se pudo inactivar la cuenta')
      }
    }
  )

  const handleInactivate = (custom) => {
    if (!custom?.id) return
    if (!window.confirm(`¿Inactivar la cuenta ${custom.code} - ${custom.name}? No se elimina; dejará de aparecer en listados activos.`)) {
      return
    }
    inactivateMutation.mutate(custom)
  }

  // Descargar plantilla CSV o XML
  const downloadTemplate = async (formato) => {
    try {
      const response = await api.get(`/accounting/accounts/descargar_plantilla_cuentas/?formato=${formato}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `plantilla_cuentas_personalizadas.${formato}`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success(`Plantilla ${formato.toUpperCase()} descargada`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al descargar plantilla')
    }
  }

  // Mutación para carga masiva
  const bulkUploadMutation = useMutation(
    async ({ file, clientId }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('client_id', clientId)
      const response = await api.post('/accounting/accounts/carga_masiva/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        setBulkResult(data)
        refreshCustomizations()
        queryClient.invalidateQueries(['chart-of-accounts-children'])
        queryClient.invalidateQueries(['chart-of-accounts-roots'])
        if (data.loaded_count > 0) {
          toast.success(`${data.loaded_count} cuenta(s) creada(s) correctamente`)
        }
        if ((data.errors?.length || 0) > 0 && (data.loaded_count || 0) === 0) {
          toast.warning('Carga completada con errores. Revise el detalle.')
        }
      },
      onError: (error) => {
        const msg = error.response?.data?.error || error.message || 'Error en la carga'
        toast.error(msg)
        setBulkResult({ error: msg })
      }
    }
  )

  const handleSubmit = (formData) => {
    if (!selectedClient) {
      toast.error('Debe seleccionar un cliente en el filtro para crear o editar una personalización')
      return
    }
    const parentId = selectedAccountPath.length > 0 ? selectedAccountPath[selectedAccountPath.length - 1] : null
    const clientId = parseInt(selectedClient, 10)
    if (isNaN(clientId)) {
      toast.error('Cliente inválido. Seleccione un cliente en el filtro.')
      return
    }
    const payload = {
      client: clientId,
      client_id: clientId,
      parent: parentId || null,
      code: (formData.codigo_personalizado || formData.code || autoGeneratedCode || '').trim(),
      name: (formData.nombre_personalizado || formData.name || '').trim(),
      account_type: formData.account_type || 'GASTO',
      is_active: formData.is_active !== false
    }
    if (formData.id) {
      customizationMutation.mutate({ ...payload, id: formData.id })
    } else {
      customizationMutation.mutate(payload)
    }
  }

  return (
    <div className="client-customization-view" data-section="personalizacion-por-cliente">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Personalización de Plan de Cuentas por Cliente</h3>
      </div>

      {/* Filtro por cliente */}
      <Card className="mb-4 border-primary">
        <Card.Header className="bg-light">
          <strong>Filtro</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Seleccionar Cliente (filtrar personalizaciones)</Form.Label>
                <Form.Select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  disabled={selectDisabled}
                  aria-label="Filtrar por cliente"
                >
                  <option value="">Todas las personalizaciones</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {(() => {
                        const esFisica = client.type_tax_regime === 'fisica' || client.type_name === 'Persona Física'
                        return esFisica
                          ? `${client.nombre || ''} ${client.apellido_paterno || ''} ${client.apellido_materno || ''}`.trim() || `Cliente #${client.id}`
                          : (client.razon_social || client.business_name || `Cliente #${client.id}`)
                      })()}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end gap-2">
              {selectedClient && (
                <>
                  <Button variant="primary" onClick={() => {
                    setSelectedCustomization(null)
                    setSelectedAccountPath([])
                    setAutoGeneratedCode('')
                    setShowForm(true)
                  }}>
                    + Nueva Personalización
                  </Button>
                  <Button variant="outline-primary" onClick={() => {
                    setShowBulkModal(true)
                    setBulkResult(null)
                  }}>
                    Cargar masivamente
                  </Button>
                </>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {customizationsError && (
        <Alert variant="danger" className="mb-3">
          Error al cargar personalizaciones: {customizationsError?.response?.data?.detail || customizationsError?.response?.data?.error || customizationsError?.message || 'Error desconocido'}
        </Alert>
      )}
      {!selectedClient && isBackgroundLoading && loadProgress.total > 0 && (
        <Alert variant="info" className="mb-3 py-2">
          <div className="d-flex justify-content-between align-items-center mb-1 small">
            <span>
              Cargando personalizaciones por cliente ({loadProgress.done}/{loadProgress.total})…
            </span>
            <Spinner animation="border" size="sm" />
          </div>
          <ProgressBar
            now={loadProgress.total ? (100 * loadProgress.done) / loadProgress.total : 0}
            style={{ height: 6 }}
          />
        </Alert>
      )}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : (
        <Card className="border-secondary">
          <Card.Header className="bg-light">
            <strong>Tabla de personalizaciones</strong>
            {selectedClient ? (
              <span className="ms-2 text-muted small">(filtrado por cliente)</span>
            ) : (
              <span className="ms-2 text-muted small">
                {isBackgroundLoading
                  ? '(generales + personalizaciones en carga…)'
                  : '(generales + personalizaciones de todos los clientes)'}
              </span>
            )}
          </Card.Header>
          <Card.Body>
            {customizations.length > 0 ? (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {customizations.map((custom) => (
                    <tr key={custom.id}>
                      <td>
                        {custom.client_id ? (() => {
                          const c = clients?.find(x => x.id == custom.client_id)
                          if (!c) return `Cliente #${custom.client_id}`
                          const esFisica = c.type_tax_regime === 'fisica' || c.type_name === 'Persona Física'
                          return esFisica
                            ? `${c.nombre || ''} ${c.apellido_paterno || ''} ${c.apellido_materno || ''}`.trim() || 'Sin nombre'
                            : (c.razon_social || c.business_name || 'Sin razón social')
                        })() : '-'}
                      </td>
                      <td>{custom.code || 'N/A'}</td>
                      <td>{custom.name || 'N/A'}</td>
                      <td>{custom.account_type || 'N/A'}</td>
                      <td>
                        <Badge bg={custom.is_active ? 'success' : 'secondary'}>
                          {custom.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedCustomization(custom)
                              if (custom.client_id) setSelectedClient(String(custom.client_id))
                              setSelectedAccountPath((custom.parent_chain_ids?.length ? custom.parent_chain_ids : (custom.parent_id ? [custom.parent_id] : [])))
                              setAutoGeneratedCode(custom.code || '')
                              setShowForm(true)
                            }}
                            disabled={!custom.client_id}
                            title={!custom.client_id ? 'Cuenta general sin cliente' : ''}
                          >
                            Editar
                          </Button>
                          {custom.client_id && custom.is_active !== false && (
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleInactivate(custom)}
                              disabled={inactivateMutation.isLoading}
                              title="Inactivar (no elimina)"
                            >
                              Inactivar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info" className="mb-0">
                {selectedClient
                  ? 'No hay personalizaciones para este cliente. Seleccione un cliente en el filtro, luego haga clic en "+ Nueva Personalización" para crear una.'
                  : 'No hay personalizaciones creadas. Seleccione un cliente en el filtro de arriba y haga clic en "+ Nueva Personalización" para crear la primera.'}
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario */}
      {showForm && selectedClient && (
        <Modal show={showForm} onHide={() => { 
          setShowForm(false)
          setSelectedCustomization(null)
          setSelectedAccountPath([])
          setAutoGeneratedCode('')
        }}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedCustomization ? 'Editar Personalización' : 'Nueva Personalización'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form
              onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.target)
                handleSubmit({
                  id: selectedCustomization?.id,
                  codigo_personalizado: formData.get('codigo_personalizado') || (selectedCustomization ? selectedCustomization.code : autoGeneratedCode) || '',
                  nombre_personalizado: formData.get('nombre_personalizado') || selectedCustomization?.name || '',
                  account_type: formData.get('account_type') || selectedCustomization?.account_type || 'GASTO',
                  is_active: formData.get('is_active') === 'on'
                })
              }}
            >
              {/* Selector de cuenta base (nivel 0) */}
              <Form.Group className="mb-3">
                <Form.Label>Cuenta Base *</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowAccountSearchModal(true)}
                  >
                    <i className="bi bi-search me-1"></i> Buscar cuenta...
                  </Button>
                </div>
                {loadingBaseAccounts ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <Form.Select 
                    name="cuenta_base"
                    value={selectedAccountPath[0] || (selectedCustomization?.cuenta_base?.id || selectedCustomization?.cuenta_base || selectedCustomization?.cuenta_base_id || '')}
                    onChange={(e) => {
                      const newBaseId = e.target.value
                      if (newBaseId) {
                        setSelectedAccountPath([parseInt(newBaseId, 10)])
                      } else {
                        setSelectedAccountPath([])
                      }
                    }}
                    required
                  >
                    <option value="">Seleccionar cuenta base...</option>
                    {baseAccounts?.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
              
              {/* Selectores dinámicos para cada nivel */}
              {selectedAccountPath.map((accountId, levelIndex) => {
                const nextLevelIndex = levelIndex + 1
                const nextLevelValue = selectedAccountPath[nextLevelIndex] || null
                
                return (
                  <AccountLevelSelector
                    key={`level-${levelIndex}`}
                    parentId={accountId}
                    levelIndex={levelIndex}
                    selectedValue={nextLevelValue}
                    onSelect={(value) => {
                      const newPath = [...selectedAccountPath]
                      if (nextLevelIndex < newPath.length) {
                        // Actualizar nivel existente y eliminar posteriores
                        setSelectedAccountPath([...newPath.slice(0, nextLevelIndex), value])
                      } else {
                        // Agregar nuevo nivel
                        setSelectedAccountPath([...newPath, value])
                      }
                    }}
                    onDeselect={() => {
                      // Eliminar este nivel y los posteriores
                      setSelectedAccountPath(selectedAccountPath.slice(0, nextLevelIndex))
                    }}
                    label={levelIndex === 0 ? 'Sub-cuenta (Opcional)' : `Nivel ${nextLevelIndex + 1} (Opcional)`}
                    user={user}
                    selectedClient={selectedClient}
                    clients={clients}
                  />
                )
              })}
              
              <Form.Group className="mb-3">
                <Form.Label>Código Personalizado</Form.Label>
                {loadingCodigo && !selectedCustomization ? (
                  <div>
                    <Form.Control
                      type="text"
                      name="codigo_personalizado"
                      value=""
                      placeholder="Generando código..."
                      disabled
                    />
                    <Form.Text className="text-muted">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generando código consecutivo...
                    </Form.Text>
                  </div>
                ) : (
                  <>
                    <Form.Control
                      type="text"
                      name="codigo_personalizado"
                      value={selectedCustomization ? (selectedCustomization.codigo_personalizado || '') : autoGeneratedCode}
                      onChange={(e) => {
                        if (selectedCustomization) {
                          // Si estamos editando, actualizar directamente en el objeto
                          setSelectedCustomization({
                            ...selectedCustomization,
                            codigo_personalizado: e.target.value
                          })
                        } else {
                          // Si es nueva, actualizar el estado
                          setAutoGeneratedCode(e.target.value)
                        }
                      }}
                      placeholder={selectedCustomization ? "Dejar vacío para usar código base" : "Se auto-generará al seleccionar cuenta base"}
                    />
                    {!selectedCustomization && autoGeneratedCode && (
                      <Form.Text className="text-muted">
                        Código auto-generado. Puedes editarlo si lo deseas.
                      </Form.Text>
                    )}
                  </>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre_personalizado"
                  defaultValue={selectedCustomization?.name || ''}
                  placeholder="Nombre de la cuenta"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de cuenta</Form.Label>
                <Form.Select name="account_type" defaultValue={selectedCustomization?.account_type || 'GASTO'}>
                  <option value="ACTIVO">Activo</option>
                  <option value="PASIVO">Pasivo</option>
                  <option value="CAPITAL">Capital</option>
                  <option value="INGRESO">Ingreso</option>
                  <option value="COSTO">Costo de venta</option>
                  <option value="GASTO">Gasto</option>
                  <option value="RESULTADO_FINANCIAMIENTO">Resultado integral de financiamiento</option>
                  <option value="CUENTAS_ORDEN">Cuentas de orden</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="is_active"
                  label="Activa"
                  defaultChecked={selectedCustomization?.is_active !== false}
                />
              </Form.Group>
              <div className="d-flex justify-content-between gap-2">
                <div>
                  {selectedCustomization?.id && selectedCustomization?.is_active !== false && (
                    <Button
                      type="button"
                      variant="outline-warning"
                      disabled={inactivateMutation.isLoading || customizationMutation.isLoading}
                      onClick={() => handleInactivate(selectedCustomization)}
                    >
                      Inactivar
                    </Button>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button variant="secondary" onClick={() => { 
                    setShowForm(false)
                    setSelectedCustomization(null)
                    setSelectedAccountPath([])
                    setAutoGeneratedCode('')
                  }}>
                    Cancelar
                  </Button>
                  <Button variant="primary" type="submit" disabled={customizationMutation.isLoading || !selectedAccountPath[0]}>
                    {customizationMutation.isLoading ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      <AccountSearchModal
        show={showAccountSearchModal}
        onHide={() => setShowAccountSearchModal(false)}
        onSelect={handleAccountSearchSelect}
        clienteId={selectedClient}
        pageSize={200}
      />

      {/* Modal de carga masiva */}
      {showBulkModal && selectedClient && (
        <Modal show={showBulkModal} onHide={() => { setShowBulkModal(false); setBulkResult(null) }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Cargar cuentas personalizadas masivamente</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Sube un archivo CSV, XML o Excel con las cuentas a crear para el cliente seleccionado. Formatos: .csv (UTF-8), .xml, .xlsx
            </p>
            <div className="mb-3">
              <Form.Label>Descargar plantilla</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" size="sm" onClick={() => downloadTemplate('csv')}>
                  Descargar CSV
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => downloadTemplate('xml')}>
                  Descargar XML
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => downloadTemplate('xlsx')}>
                  Descargar Excel
                </Button>
              </div>
            </div>
            <Form
              onSubmit={(e) => {
                e.preventDefault()
                const fileInput = e.target.querySelector('input[type="file"]')
                const file = fileInput?.files?.[0]
                if (!file) {
                  toast.error('Selecciona un archivo')
                  return
                }
                bulkUploadMutation.mutate({ file, clientId: selectedClient })
              }}
            >
              <Form.Group className="mb-3">
                <Form.Label>Archivo</Form.Label>
                <Form.Control
                  type="file"
                  accept=".csv,.xml,.xlsx"
                  required
                />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => { setShowBulkModal(false); setBulkResult(null) }}>
                  Cerrar
                </Button>
                <Button variant="primary" type="submit" disabled={bulkUploadMutation.isLoading}>
                  {bulkUploadMutation.isLoading ? 'Cargando...' : 'Cargar'}
                </Button>
              </div>
            </Form>

            {/* Resultado de la carga */}
            {bulkResult && !bulkResult.error && (
              <Alert variant={bulkResult.errors?.length > 0 ? 'warning' : 'success'} className="mt-3">
                <strong>Resultado:</strong>
                <ul className="mb-0 mt-2">
                  <li>Creadas: {bulkResult.loaded_count ?? 0}</li>
                  <li>Omitidas (duplicadas): {bulkResult.skipped_count ?? 0}</li>
                  {(bulkResult.errors?.length ?? 0) > 0 && (
                    <li>Errores: {bulkResult.errors.length}</li>
                  )}
                </ul>
                {bulkResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <strong>Detalle de errores:</strong>
                    <ul className="small mb-0 mt-1" style={{ maxHeight: 150, overflowY: 'auto' }}>
                      {bulkResult.errors.map((err, idx) => (
                        <li key={idx}>Fila {err.fila}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Alert>
            )}
            {bulkResult?.error && (
              <Alert variant="danger" className="mt-3 mb-0">{bulkResult.error}</Alert>
            )}
          </Modal.Body>
        </Modal>
      )}
    </div>
  )
}

export default ClientCustomization

